import React from 'react';
import PropTypes from 'prop-types';

import {Box, Chip, Stack, Typography} from 'views/components';
import {translate} from 'views/module';

import FormResponsesCard from './FormResponsesCard';
import FormsEmptyState from './FormsEmptyState';

const Forms = ({clientForms}) => {
  const forms = React.useMemo(() => {
    const originalScheduleFormKey = 'CLIENT_SCHEDULE_INTAKE_FORM';
    const mergedScheduleFormKeys = new Set([
      'CLIENT_SCHEDULE_ONCALL_INTAKE_FORM',
      'CLIENT_SCHEDULE_FLEXIBLE_INTAKE_FORM',
    ]);

    const sortFormsByCreatedAt = (formList = []) => {
      return [...formList].sort((a, b) => {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
    };

    const source = Array.isArray(clientForms) ? clientForms : [];
    const sortedForms = sortFormsByCreatedAt(source);
    const originalScheduleForm = sortedForms.find((form) => form?.formKey === originalScheduleFormKey);
    const mergeScheduleForms = sortedForms.filter((form) => mergedScheduleFormKeys.has(form?.formKey));

    if (!originalScheduleForm || !mergeScheduleForms.length) {
      return sortedForms;
    }

    const mergedScheduleEvents = mergeScheduleForms.flatMap((form) => {
      const answers = Object.values(form?.answers || {});
      return answers
          .filter((answer) => answer?.fieldType === 'schedule')
          .flatMap((answer) => answer?.value?.events || []);
    });
    const [scheduleQuestionId, scheduleAnswer] = Object.entries(originalScheduleForm?.answers || {}).find(([, answer]) => answer?.fieldType === 'schedule');
    const mergedOriginalScheduleForm = {
      ...originalScheduleForm,
      answers: {
        ...originalScheduleForm.answers,
        [scheduleQuestionId]: {
          ...scheduleAnswer,
          value: {
            ...scheduleAnswer.value,
            events: [...scheduleAnswer.value.events, ...mergedScheduleEvents],
          },
        },
      },
    };

    const formsWithMergedOriginal = sortedForms
        .filter((form) => !mergedScheduleFormKeys.has(form?.formKey))
        .map((form) => (
          form?.formKey === originalScheduleFormKey ?
          mergedOriginalScheduleForm :
          form
        ));

    return sortFormsByCreatedAt(formsWithMergedOriginal);
  }, [clientForms]);
  const hasForms = forms.length > 0;

  return (
    <Box
      sx={{
        p: {xs: 1, md: 1.5},
        borderRadius: 3,
        border: '1px solid rgba(255,255,255,0.4)',
      }}
    >
      <Stack spacing={2}>
        <Stack
          direction={{xs: 'column', sm: 'row'}}
          justifyContent="space-between"
          alignItems={{xs: 'flex-start', sm: 'center'}}
          spacing={1}
        >
          <Typography variant="h5" sx={{fontWeight: 700}}>
            {translate('client.ClientDetail.FormSettings.Title')}
          </Typography>
          <Chip
            label={`${translate('client.ClientDetail.FormSettings.FormsFeed.Responses')} | ${forms.length}`}
            color="primary"
            variant="outlined"
            sx={{fontWeight: 600, letterSpacing: 0.4}}
          />
        </Stack>

        {
          hasForms ?
          forms.map((form, index) => (
            <FormResponsesCard
              key={form?.formKey || form?.updatedAt || index}
              form={form}
            />
          )) :
          <FormsEmptyState />
        }
      </Stack>
    </Box>
  );
};

Forms.propTypes = {
  clientForms: PropTypes.array,
};

export default React.memo(Forms);
